import React from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import ReactMarkdown from "react-markdown";
import newsletters from "./newsletterData";
import "../Styles/Blog.css"; // Reuse blog content spacing
import "../Styles/Newsletter.css";

const calculateReadTime = (content) => {
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
};

export default function NewsletterPost() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const { slug } = useParams();
  const newsletter = newsletters.find((n) => n.slug === slug);

  if (!newsletter) {
    return (
      <div className="blog-not-found container py-5 text-center">
        <h2 className="text-dark mb-3">Newsletter issue not found.</h2>
        <Link id="newsletter-post-not-found-back-link" to="/newsletters" className="lmbtn text-decoration-none">
          ← Back to Newsletters
        </Link>
      </div>
    );
  }

  const readTime = calculateReadTime(newsletter.content);

  // Get related newsletters (exclude current, take up to 3)
  const relatedNewsletters = newsletters
    .filter((n) => n.slug !== slug)
    .sort(() => 0.5 - Math.random())
    .slice(0, 3);

  const shareUrl = `https://veabroad.com/newsletter/${newsletter.slug}`;
  const shareTitle = encodeURIComponent(newsletter.title);

  return (
    <>
      <Helmet>
        <title>{newsletter.title} | VE Abroad Newsletter</title>
        <meta name="description" content={newsletter.excerpt} />
        <link rel="canonical" href={`https://veabroad.com/newsletter/${newsletter.slug}`} />
        <meta property="og:title" content={`${newsletter.title} | VE Abroad`} />
        <meta property="og:description" content={newsletter.excerpt} />
        <meta property="og:image" content={newsletter.image} />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${newsletter.title} | VE Abroad`} />
        <meta name="twitter:description" content={newsletter.excerpt} />
        <meta name="twitter:image" content={newsletter.image} />
        <meta name="keywords" content={`${newsletter.category}, study visa, immigration, scholarships, VE Abroad`} />
      </Helmet>

      <article className="newsletter-post-container">
        <Link id="newsletter-post-back-link" to="/newsletters" className="blog-back-link text-decoration-none d-inline-flex align-items-center mb-4">
          <i className="bi bi-arrow-left me-1"></i> Back to Newsletters
        </Link>

        <div className="blog-post-header">
          <span className="blog-category">{newsletter.category}</span>
          <span className="blog-read-time">{readTime} min read</span>
        </div>

        <h1 className="fw-extrabold text-dark display-5 mb-3">{newsletter.title}</h1>
        
        <div className="blog-author-meta mb-4">
          <p className="blog-date mb-0 text-muted">
            <i className="bi bi-calendar3 me-1"></i> {newsletter.date} • Published by Vidya Education Abroad Team
          </p>
        </div>

        <div className="newsletter-post-hero">
          <img src={newsletter.image} alt={newsletter.title} loading="eager" />
        </div>
        
        {/* Social Share */}
        <div className="blog-social-share bg-white p-3 rounded-3 shadow-sm border border-light d-flex align-items-center mb-4">
          <span className="fw-semibold text-dark me-3">Share this Update:</span>
          <div className="d-flex gap-2 flex-wrap">
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" className="share-icon facebook text-decoration-none">
              <i className="bi bi-facebook me-1"></i> Facebook
            </a>
            <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${shareTitle}`} target="_blank" rel="noopener noreferrer" className="share-icon linkedin text-decoration-none">
              <i className="bi bi-linkedin me-1"></i> LinkedIn
            </a>
            <a href={`https://api.whatsapp.com/send?text=${shareTitle} ${shareUrl}`} target="_blank" rel="noopener noreferrer" className="share-icon whatsapp text-decoration-none">
              <i className="bi bi-whatsapp me-1"></i> WhatsApp
            </a>
          </div>
        </div>

        {/* Content Render */}
        <div className="newsletter-post-content blog-post-content mt-4">
          <ReactMarkdown>{newsletter.content}</ReactMarkdown>
        </div>

        {/* CTA Banner */}
        <div className="blog-cta my-5 p-4 text-center rounded-4 shadow-md text-white" style={{ background: "linear-gradient(135deg, #1d4e50, #36948c)" }}>
          <h4 className="fw-bold mb-2">Planning to Study, Work or Migrate Abroad?</h4>
          <p className="text-white-50 small mb-4">
            Get professional advice tailored to your credentials. Speak to a licensed Vidya Education Abroad consultant today for free.
          </p>
          <Link id="newsletter-post-consultation-link" to="/consultationForm" className="lmbtn text-white text-decoration-none px-4 py-2.5 rounded-pill shadow-sm">
            Get Free Consultation <i className="bi bi-arrow-right-short ms-1"></i>
          </Link>
        </div>

        {/* Related Newsletters List */}
        {relatedNewsletters.length > 0 && (
          <div className="related-posts-section">
            <h2 className="fw-bold text-dark border-bottom pb-2 mb-4" style={{ fontSize: "1.8rem" }}>Recommended Resources</h2>
            <div className="row g-4">
              {relatedNewsletters.map((relNews) => (
                <div className="col-md-4" key={relNews.slug}>
                  <Link to={`/newsletter/${relNews.slug}`} className="blog-card-link text-decoration-none">
                    <div className="blog-card shadow-sm border border-light h-100 hover-lift d-flex flex-column" style={{ borderRadius: "16px", overflow: "hidden" }}>
                      <img src={relNews.image} alt={relNews.title} className="w-100" style={{ height: "150px", objectFit: "cover" }} loading="lazy" />
                      <div className="blog-card-body p-3 flex-grow-1 d-flex flex-column">
                        <span className="blog-category mb-2" style={{ fontSize: "0.7rem" }}>{relNews.category}</span>
                        <h3 className="h6 fw-bold text-dark line-clamp-2 mb-2" style={{ fontSize: "0.95rem", lineHeight: "1.4" }}>
                          {relNews.title}
                        </h3>
                        <div className="text-muted small mt-auto" style={{ fontSize: "0.78rem" }}>
                          {relNews.date}
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}
